// Create/edit dialogs for escalation policies, services and schedules, replacing
// upstream's generic forms ("Repeat Count", "Submit") with plain choices.
import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { useLocation } from 'wouter'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Check, Remove } from '@mui/icons-material'
import { useServiceKinds } from './reports'
import { SeverityChip } from './AlertsPage'
import { actionLabel } from './ListPages'
import { useBrand } from './ui'
import { placeCity, usePlaces, useTimezones, zoneName } from './tz'

// GoAlert's urql client suspends by default; these dialogs render their own states.
const noSuspense = { suspense: false }

const gqlMessage = (e?: { message: string }): string =>
  (e?.message ?? '').replace('[GraphQL] ', '')

// RepeatChoice is "If still nobody acknowledged: Stop | Start over ×N", used on
// the policy page and in the policy dialog. 0 = stop, 1..5 = more rounds.
export function RepeatChoice(props: {
  value: number
  onChange: (n: number) => void
  disabled?: boolean
}): React.JSX.Element {
  const c = useBrand()
  const n = props.value
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      <Chip
        label='Stop'
        color={n === 0 ? 'primary' : 'default'}
        variant={n === 0 ? 'filled' : 'outlined'}
        disabled={props.disabled}
        onClick={() => n && props.onChange(0)}
      />
      <Chip
        label='Start over from step 1'
        color={n > 0 ? 'primary' : 'default'}
        variant={n > 0 ? 'filled' : 'outlined'}
        disabled={props.disabled}
        onClick={() => !n && props.onChange(1)}
      />
      {n > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size='small'
            aria-label='Fewer times'
            disabled={props.disabled || n <= 1}
            onClick={() => props.onChange(n - 1)}
            sx={{ border: `1px solid ${c.line}` }}
          >
            <Remove fontSize='small' />
          </IconButton>
          <Typography sx={{ minWidth: 110, textAlign: 'center', fontSize: 14 }}>
            {n} more time{n > 1 ? 's' : ''}
          </Typography>
          <IconButton
            size='small'
            aria-label='More times'
            disabled={props.disabled || n >= 5}
            onClick={() => props.onChange(n + 1)}
            sx={{ border: `1px solid ${c.line}` }}
          >
            <Add fontSize='small' />
          </IconButton>
          <Typography sx={{ fontSize: 13, color: c.muted, ml: 1 }}>
            {n + 1} rounds in total
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------- policies

const policyQuery = gql`
  query ErecordsPolicyForm($id: ID!) {
    escalationPolicy(id: $id) {
      id
      name
      description
      repeat
    }
  }
`
const createPolicy = gql`
  mutation ErecordsCreatePolicy($input: CreateEscalationPolicyInput!) {
    createEscalationPolicy(input: $input) {
      id
    }
  }
`
const updatePolicy = gql`
  mutation ErecordsUpdatePolicyForm($input: UpdateEscalationPolicyInput!) {
    updateEscalationPolicy(input: $input)
  }
`

export function PolicyDialog(props: {
  policyID?: string // edit when set
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const [, navigate] = useLocation()
  const [q] = useQuery({
    query: policyQuery,
    variables: { id: props.policyID ?? '' },
    pause: !props.policyID,
    context: noSuspense,
  })
  const [cm, create] = useMutation(createPolicy)
  const [um, update] = useMutation(updatePolicy)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [repeat, setRepeat] = useState(0)
  const p = q.data?.escalationPolicy
  useEffect(() => {
    if (!p) return
    setName(p.name)
    setDescription(p.description)
    setRepeat(p.repeat)
  }, [p?.id])

  const editing = Boolean(props.policyID)
  const status = editing ? um : cm
  const save = (): void => {
    const input = { name: name.trim(), description: description.trim(), repeat }
    if (editing) {
      update(
        { input: { id: props.policyID, ...input } },
        { additionalTypenames: ['EscalationPolicy'] },
      ).then((r) => !r.error && props.onClose())
      return
    }
    create({ input }, { additionalTypenames: ['EscalationPolicy'] }).then(
      (r) => {
        if (r.error) return
        props.onClose()
        const id = r.data?.createEscalationPolicy?.id
        if (id) navigate(`/escalation-policies/${id}`)
      },
    )
  }

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {editing ? 'Edit escalation policy' : 'New escalation policy'}
      </DialogTitle>
      <DialogContent>
        {!editing && (
          <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
            A policy says who is notified, in which order, and how long to wait
            between steps. You add the steps next.
          </Typography>
        )}
        <TextField
          fullWidth
          label='Name'
          placeholder='e.g. Platform on-call'
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: editing ? 1 : 0, mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label='What is it for? (optional)'
          placeholder='e.g. Critical Grafana alerts: call the on-call engineer, then post to Teams'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2.5 }}
        />
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          If still nobody acknowledged after the last step
        </Typography>
        <RepeatChoice value={repeat} onChange={setRepeat} />
        {status.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(status.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!name.trim() || status.fetching || (editing && !p)}
          onClick={save}
        >
          {editing ? 'Save' : 'Create and add steps'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------- services

const serviceQuery = gql`
  query ErecordsServiceForm($id: ID!) {
    service(id: $id) {
      id
      name
      description
      escalationPolicy {
        id
      }
    }
  }
`
const policiesQuery = gql`
  query ErecordsServiceFormPolicies {
    escalationPolicies(input: { first: 100 }) {
      nodes {
        id
        name
        steps {
          id
          actions {
            type
            displayInfo {
              ... on DestinationDisplayInfo {
                text
              }
            }
          }
        }
      }
    }
  }
`
const createService = gql`
  mutation ErecordsCreateService($input: CreateServiceInput!) {
    createService(input: $input) {
      id
    }
  }
`
const updateService = gql`
  mutation ErecordsUpdateServiceForm($input: UpdateServiceInput!) {
    updateService(input: $input)
  }
`

type PolicyOption = {
  id: string
  name: string
  steps: {
    id: string
    actions: { type: string; displayInfo: { text?: string } }[]
  }[]
}

const NEW_POLICY = 'new'

// who a policy notifies first, then later, in a few words
function policyLine(p: PolicyOption): string {
  if (!p.steps.length) return 'No steps yet: notifies nobody'
  const who = (i: number): string =>
    p.steps[i].actions.map(actionLabel).join(', ') || 'nobody'
  return p.steps.length === 1
    ? `Notifies ${who(0)}`
    : `Notifies ${who(0)}, then ${who(1)}${p.steps.length > 2 ? ', …' : ''}`
}

function Choice(props: {
  selected: boolean
  onSelect: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  meta?: React.ReactNode
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Box
      role='radio'
      aria-checked={props.selected}
      tabIndex={0}
      onClick={props.onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          props.onSelect()
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: '12px',
        cursor: 'pointer',
        border: `1.5px solid ${props.selected ? c.ink : c.line}`,
        bgcolor: props.selected ? c.soft : 'transparent',
        '&:hover': { bgcolor: c.hover },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          border: `1.5px solid ${props.selected ? c.ink : c.muted}`,
          bgcolor: props.selected ? c.ink : 'transparent',
          color: c.surface,
          '& svg': { fontSize: 12 },
        }}
      >
        {props.selected && <Check />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography sx={{ fontSize: 12, color: c.muted }} noWrap>
            {props.subtitle}
          </Typography>
        )}
      </Box>
      {props.meta}
    </Box>
  )
}

export function ServiceDialog(props: {
  serviceID?: string // edit when set
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const [, navigate] = useLocation()
  const kinds = useServiceKinds()
  const [sq] = useQuery({
    query: serviceQuery,
    variables: { id: props.serviceID ?? '' },
    pause: !props.serviceID,
    context: noSuspense,
  })
  const [pq] = useQuery({ query: policiesQuery, context: noSuspense })
  const [cm, create] = useMutation(createService)
  const [um, update] = useMutation(updateService)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [policy, setPolicy] = useState('')
  const [report, setReport] = useState(false)
  const s = sq.data?.service
  useEffect(() => {
    if (!s) return
    setName(s.name)
    setDescription(s.description)
    setPolicy(s.escalationPolicy?.id ?? '')
  }, [s?.id])
  const wasReport = Boolean(
    props.serviceID &&
      kinds.services.find((k) => k.id === props.serviceID)?.report,
  )
  useEffect(() => {
    if (props.serviceID) setReport(wasReport)
  }, [wasReport])

  const editing = Boolean(props.serviceID)
  const status = editing ? um : cm
  const policies: PolicyOption[] = (pq.data?.escalationPolicies?.nodes ?? [])
    .slice()
    .sort((a: PolicyOption, b: PolicyOption) => a.name.localeCompare(b.name))
  const valid = name.trim() !== '' && policy !== ''

  const save = (): void => {
    const base = { name: name.trim(), description: description.trim() }
    if (editing && props.serviceID) {
      const id = props.serviceID
      update(
        { input: { id, ...base, escalationPolicyID: policy } },
        { additionalTypenames: ['Service'] },
      ).then(async (r) => {
        if (r.error) return
        if (report !== wasReport) await kinds.setReport(id, report)
        props.onClose()
      })
      return
    }
    create(
      {
        input: {
          ...base,
          ...(policy === NEW_POLICY
            ? { newEscalationPolicy: { name: base.name, repeat: 0 } }
            : { escalationPolicyID: policy }),
        },
      },
      { additionalTypenames: ['Service', 'EscalationPolicy'] },
    ).then(async (r) => {
      if (r.error) return
      const id = r.data?.createService?.id
      if (id && report) await kinds.setReport(id, true)
      props.onClose()
      if (id) navigate(`/services/${id}`)
    })
  }

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{editing ? 'Edit service' : 'New service'}</DialogTitle>
      <DialogContent>
        {!editing && (
          <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
            A service receives alerts from a sender (Grafana, BVT, …) and hands
            them to an escalation policy.
          </Typography>
        )}
        <TextField
          fullWidth
          label='Name'
          placeholder='e.g. Grafana Alerts - critical'
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: editing ? 1 : 0, mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label='Description (optional)'
          placeholder='What sends here and what it means'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText='Markdown works.'
          sx={{ mb: 2 }}
        />

        <Typography sx={{ fontWeight: 600, mb: 1 }}>It receives</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
          <Chip
            label='Alerts'
            color={!report ? 'primary' : 'default'}
            variant={!report ? 'filled' : 'outlined'}
            onClick={() => setReport(false)}
          />
          <Chip
            label='Reports'
            color={report ? 'primary' : 'default'}
            variant={report ? 'filled' : 'outlined'}
            onClick={() => setReport(true)}
          />
        </Box>
        <Typography sx={{ fontSize: 13, color: c.muted, mb: 2.5 }}>
          {report
            ? 'Information such as test runs: listed under Reports and closed automatically. Failures still show up as alerts.'
            : 'Problems someone must acknowledge: they escalate until someone does.'}
        </Typography>

        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          Escalation policy
        </Typography>
        <Box
          role='radiogroup'
          aria-label='Escalation policy'
          sx={{ display: 'grid', gap: 0.75, maxHeight: 260, overflowY: 'auto' }}
        >
          {!pq.data && (
            <Typography sx={{ color: c.muted, fontSize: 14 }}>
              Loading…
            </Typography>
          )}
          {policies.map((p) => (
            <Choice
              key={p.id}
              selected={policy === p.id}
              onSelect={() => setPolicy(p.id)}
              title={p.name}
              subtitle={policyLine(p)}
              meta={<SeverityChip name={p.name} />}
            />
          ))}
          {!editing && (
            <Choice
              selected={policy === NEW_POLICY}
              onSelect={() => setPolicy(NEW_POLICY)}
              title={`New policy “${name.trim() || 'same name as the service'}”`}
              subtitle='Starts empty: add who to notify on the policy page'
            />
          )}
        </Box>

        {status.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(status.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!valid || status.fetching || (editing && !s)}
          onClick={save}
        >
          {editing ? 'Save' : 'Create service'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------- schedules

const scheduleQuery = gql`
  query ErecordsScheduleForm($id: ID!) {
    schedule(id: $id) {
      id
      name
      description
      timeZone
    }
  }
`
const createSchedule = gql`
  mutation ErecordsCreateSchedule($input: CreateScheduleInput!) {
    createSchedule(input: $input) {
      id
    }
  }
`
const updateSchedule = gql`
  mutation ErecordsUpdateScheduleForm($input: UpdateScheduleInput!) {
    updateSchedule(input: $input)
  }
`

const allZones = (): string[] => {
  const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
    .supportedValuesOf
  return f ? f('timeZone') : []
}

// ScheduleDialog creates or edits a schedule: name, purpose and the time zone
// its shifts are written in (each person still sees their own clock).
export function ScheduleDialog(props: {
  scheduleID?: string // edit when set
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const [, navigate] = useLocation()
  const zones = useTimezones()
  const places = usePlaces()
  const [q] = useQuery({
    query: scheduleQuery,
    variables: { id: props.scheduleID ?? '' },
    pause: !props.scheduleID,
    context: noSuspense,
  })
  const [cm, create] = useMutation(createSchedule)
  const [um, update] = useMutation(updateSchedule)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // new schedules default to UTC; the chips offer where the team is
  const [zone, setZone] = useState('UTC')
  const sch = q.data?.schedule
  useEffect(() => {
    if (!sch) return
    setName(sch.name)
    setDescription(sch.description)
    setZone(sch.timeZone)
  }, [sch?.id])

  // quick picks: where the team is (from Entra), one chip per zone
  const team = new Map<string, string>([['UTC', 'UTC']])
  for (const [id, z] of Object.entries(zones)) {
    if (!team.has(z)) team.set(z, placeCity(z, places[id]))
  }
  const options = ['UTC', ...allZones().filter((z) => z !== 'UTC')]

  const editing = Boolean(props.scheduleID)
  const status = editing ? um : cm
  const save = (): void => {
    const input = {
      name: name.trim(),
      description: description.trim(),
      timeZone: zone,
    }
    if (editing) {
      update(
        { input: { id: props.scheduleID, ...input } },
        { additionalTypenames: ['Schedule'] },
      ).then((r) => !r.error && props.onClose())
      return
    }
    create({ input }, { additionalTypenames: ['Schedule'] }).then((r) => {
      if (r.error) return
      props.onClose()
      const id = r.data?.createSchedule?.id
      if (id) navigate(`/schedules/${id}`)
    })
  }

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{editing ? 'Edit schedule' : 'New schedule'}</DialogTitle>
      <DialogContent>
        {!editing && (
          <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
            A schedule says who is on call when. Escalation steps can notify
            &ldquo;whoever this schedule says is on call&rdquo;. You add people
            and shifts next.
          </Typography>
        )}
        <TextField
          fullWidth
          label='Name'
          placeholder='e.g. Platform on-call'
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: editing ? 1 : 0, mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label='What is it for? (optional)'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2.5 }}
        />

        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Time zone</Typography>
        <Typography sx={{ fontSize: 13, color: c.muted, mb: 1 }}>
          Shift times are written in this zone. Everyone still sees them in
          their own time.
        </Typography>
        {team.size > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
            {Array.from(team.entries()).map(([z, city]) => (
              <Chip
                key={z}
                label={z === 'UTC' ? 'UTC' : `${city} · ${zoneName(z)}`}
                color={zone === z ? 'primary' : 'default'}
                variant={zone === z ? 'filled' : 'outlined'}
                onClick={() => setZone(z)}
              />
            ))}
          </Box>
        )}
        <Autocomplete
          size='small'
          options={options}
          value={zone}
          disableClearable
          onChange={(_, v) => v && setZone(v)}
          getOptionLabel={(z) =>
            z === 'UTC' ? 'UTC' : `${zoneName(z)} · ${z.replace(/_/g, ' ')}`
          }
          renderInput={(params) => (
            <TextField {...params} placeholder='Search a city or zone' />
          )}
        />

        {status.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(status.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={
            !name.trim() || !zone || status.fetching || (editing && !sch)
          }
          onClick={save}
        >
          {editing ? 'Save' : 'Create and add people'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
